import type React from 'react';
import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import {
  FormControl,
  FormErrorMessage,
  Box,
  Flex,
  Spinner,
  Center,
  Icon,
} from '@chakra-ui/react';
import { FormCustomLabel } from '@/components/formBuilder/components';
import { InterviewDetailsAPI } from '@/components/shared/interview-scheduler/SchedulerDetails/api/InterviewDetailsAPI';
import Select, { components } from 'react-select';
import { getCustomStyles } from '@/theme/components/select';
import { KText } from '@/ui-library/components';
import { useField } from 'formik';
import { useQuery } from '@tanstack/react-query';
import { useShowToast } from '@/hooks';
import { SchMeetingRoomMenu } from '@/components/shared/interview-scheduler/SchedulerDetails/InterviewSchedulerForm/Fields/SchMeetingRoom/SchMeetingRoomMenu';
import { fontSizes } from '@/theme/foundations/fontSizes';
import { fontWeights } from '@/theme/foundations/fontWeights';
import { colors } from '@/theme/foundations/colors';
import { FaceSad } from '@kula-ai/icons/dual-tone/FaceSad';
import { useHistory } from 'react-router-dom';
import { usePermission } from '@/ui/permission';
import { useToast } from '@chakra-ui/react';
import { useAuth } from '@/hooks';
// Type definitions
interface Room {
  room_email: string;
  room_name: string;
  capacity: number;
  available: boolean | null;
  provider?: 'gcal' | 'mscal';
}

interface MeetingRoomsResponse {
  rooms: Record<string, Room[]>;
}

interface UuidResponse {
  fetch_uuid: string;
}

interface PollResponse {
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  results?: MeetingRoomsResponse;
  data?: {
    status: 'PENDING' | 'SUCCESS' | 'FAILED';
    results?: MeetingRoomsResponse;
  };
}

// Normalize API response: backend may return { data: { status, results } } or flat { status, results }
const getPollPayload = (raw: PollResponse | undefined) => {
  if (!raw) return { status: undefined, results: undefined };
  const nested = raw.data;
  return {
    status: nested?.status ?? raw.status,
    results: nested?.results ?? raw.results,
  };
};

interface Option {
  value: string;
  label: string;
  room_name: string;
  capacity: number;
  available: boolean;
}

interface OptionGroup {
  label: string;
  options: Option[];
}

interface AccordionOption {
  id: string;
  value: string;
  label: string;
  name: string;
  capacity: number;
  available: boolean;
}

interface AccordionGroup {
  title: string;
  count: number;
  options: AccordionOption[];
}

interface SchMeetingRoomProps {
  name?: string;
  isMulti?: boolean;
  updateSharedData?: (data: any) => void;
  registerForUpdates: any;
  interviewDataRef: any;
  jobStageActivityId?: number;
  isBulkManual?: boolean;
  cacheKey?: string;
  localCacheData?: any;
  isEdit?: boolean;
}

// Format room data into select options
const formatRoomsToOptions = (
  roomsData: MeetingRoomsResponse | undefined
): OptionGroup[] => {
  if (!roomsData?.rooms) return [];

  return Object.entries(roomsData.rooms).map(([building, rooms]) => ({
    label: building,
    options: rooms.map((room) => ({
      ...room,
      value: room.room_email,
      label: building ? `${room.room_name} - ${building}` : room.room_name,
    })),
  }));
};

// Format room data for accordion groups
const formatRoomsToAccordionGroups = (
  roomsData: MeetingRoomsResponse | undefined
): AccordionGroup[] => {
  if (!roomsData?.rooms) return [];

  return Object.entries(roomsData.rooms).map(([building, rooms]) => ({
    title: building || 'No location',
    count: rooms.length,
    options: rooms.map((room) => ({
      id: room.room_email,
      value: room.room_email,
      label: room.room_name,
      name: room.room_name,
      capacity: room.capacity,
      available: room.available,
    })),
  }));
};

const MAX_RETRIES = 10;
const POLL_INTERVAL = 2000;

// Custom filter function for React Select to work with grouped options
const customFilterOption = (option, inputValue) => {
  // Handle group options
  if (option.options) {
    // This is a group, check if any of its options match
    return option.options.some((opt) =>
      opt.room_name?.toLowerCase().includes(inputValue?.toLowerCase())
    );
  }

  // Handle individual options
  return option.room_name?.toLowerCase().includes(inputValue?.toLowerCase());
};

// Custom loading message component similar to interviewers select
const CustomLoadingMessage = () => {
  return (
    <Center py="20px">
      <Spinner color="primary-800" size="md" thickness="3px" />
    </Center>
  );
};

// Custom IndicatorSeparator component that renders nothing
const CustomIndicatorSeparator = () => null;

// Custom MultiValueLabel component to show text in red for unavailable rooms
const CustomMultiValueLabel = (props) => {
  const { data } = props;
  const isUnavailable = data.available === false;

  return (
    <components.MultiValueLabel {...props}>
      <Flex alignItems="center" gap="4px">
        {isUnavailable && (
          <Box w="8px" h="8px" borderRadius="full" bg="#C03744" mx="3px" />
        )}
        <KText fontWeight="medium" fontSize="sm">
          {props.children}
        </KText>
      </Flex>
    </components.MultiValueLabel>
  );
};

const getErrorMap = (isMscal: boolean) => ({
  err_no_meeting_room_integration: isMscal
    ? 'Microsoft admin consent is not connected'
    : 'Google Workspace is not connected',
});

export const SchMeetingRoom: React.FC<SchMeetingRoomProps> = ({
  name = 'meeting_room_ids',
  isMulti = true,
  updateSharedData,
  registerForUpdates,
  interviewDataRef,
  isBulkManual,
  cacheKey,
  localCacheData,
  isEdit = false,
}) => {
  const [field, meta, helpers] = useField(name);
  const { error, touched } = meta;
  const { setTouched, setValue } = helpers;
  const [startTime, setStartTime] = useState(
    interviewDataRef?.current?.bookedSlot?.start
  );
  const [endTime, setEndTime] = useState(
    interviewDataRef?.current?.bookedSlot?.end
  );
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [inputValue, setInputValue] = useState('');
  const toast = useShowToast();
  const value = field.value;
  const history = useHistory();
  const isMeetingRoomEnabled = usePermission({
    permission: 'api_ats_v1_meeting_rooms.show_setting?',
  });
  const chakraToast = useToast();
  const errorToastShownRef = useRef(false);
  const { currUser } = useAuth();

  const initialValuesRef = useRef({
    startTime: interviewDataRef?.current?.bookedSlot?.start,
    endTime: interviewDataRef?.current?.bookedSlot?.end,
    selectedRoomIds: value || [],
  });

  const getMeetinRoomsErrMsg = () => {
    const isMscal = currUser?.connected_calendar?.mscal;
    const providerName = isMscal ? 'Outlook' : 'Google Workspace';
    if (!isMeetingRoomEnabled) {
      return `${providerName} re-authentication required to view available meeting rooms. Ask an admin to re-authenticate.`;
    } else {
      return (
        <>
          Re-authenticate {providerName} to view available meeting rooms.{' '}
          <Box
            as="span"
            textDecoration="underline"
            cursor="pointer"
            onClick={() => {
              history.push('/settings/meeting-rooms');
            }}
          >
            Go to settings.
          </Box>
        </>
      );
    }
  };

  const hasValuesChanged = () => {
    if (
      !initialValuesRef.current.startTime ||
      !initialValuesRef.current.endTime
    )
      return false;

    const timeChanged =
      startTime !== initialValuesRef.current.startTime ||
      endTime !== initialValuesRef.current.endTime;

    // For room changes, we need to check if the actual room selection is different
    const currentRooms = Array.isArray(value) ? value : [value];
    const initialRooms = initialValuesRef.current.selectedRoomIds;

    const roomChanged =
      currentRooms.length !== initialRooms.length ||
      currentRooms.some((room) => !initialRooms.includes(room));

    return timeChanged || roomChanged;
  };

  // Step 1: Get the UUID for meeting rooms
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- pre-existing; behavior preserved (extraction move)
  const { data: uuidData, isFetching: isUuidLoading } = useQuery<UuidResponse>({
    queryKey: ['meetingRoomsUuid', startTime, endTime],
    queryFn: async () => {
      const response = await InterviewDetailsAPI.getMeetingRoomsUuid({
        start_time: startTime,
        end_time: endTime,
      });

      if ('errors' in response) {
        const errorMessage = response?.errors[0];
        if (errorMessage) {
          toast({
            title: 'Failed to get meeting rooms',
            description:
              typeof errorMessage === 'string'
                ? (getErrorMap(!!currUser?.connected_calendar?.mscal)[
                    errorMessage
                  ] ??
                  errorMessage?.replaceAll('_', ' ') ??
                  '')
                : 'Something went wrong',
            status: 'error',
          });
        }
      }

      return response?.data;
    },
    gcTime: 0,
    enabled: isBulkManual ? !localCacheData : true,
  });
  // Reset error toast ref when query dependencies change
  useEffect(() => {
    errorToastShownRef.current = false;
  }, [uuidData?.fetch_uuid]);

  // Step 2: Fetch meeting rooms data using the UUID
  const { data: pollResponse, isFetching: isPollLoading } =
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- pre-existing; behavior preserved (extraction move)
    useQuery<PollResponse>({
      queryKey: ['meetingRooms', uuidData?.fetch_uuid],
      queryFn: async () => {
        if (!uuidData?.fetch_uuid) {
          throw new Error('No UUID available');
        }

        const response = await InterviewDetailsAPI.getPollMeetingRooms({
          uuId: uuidData.fetch_uuid,
        });
        if (cacheKey && response?.data?.status === 'SUCCESS') {
          localStorage.setItem(cacheKey, JSON.stringify(response));
        }
        chakraToast.closeAll();
        if (
          response?.data?.status === 'FAILED' &&
          // @ts-expect-error -- pre-existing type debt (extraction move)
          response?.errors?.includes('err_google_auth_error')
        ) {
          toast({
            title: getMeetinRoomsErrMsg(),
            status: 'info',
          });
          return;
        }

        return response?.data;
      },
      enabled: !!uuidData?.fetch_uuid,
      refetchInterval: (queryState) => {
        const { dataUpdateCount: numberOfApiCallsMade, data: apiResponse } =
          // eslint-disable-next-line no-unsafe-optional-chaining -- pre-existing; behavior preserved (extraction move)
          queryState?.state;
        const { status: pollStatus } = getPollPayload(
          apiResponse as PollResponse
        );
        if (numberOfApiCallsMade > MAX_RETRIES || pollStatus === 'FAILED') {
          // Only show toast if it hasn't been shown before
          if (!errorToastShownRef.current) {
            errorToastShownRef.current = true;
            toast({
              title:
                numberOfApiCallsMade > MAX_RETRIES
                  ? 'Unable to get calendar data - Maximum polling attempts exceeded'
                  : 'Unable to get calendar data',
              status: 'error',
            });
          }
          return false;
        }
        return pollStatus === 'PENDING' ? POLL_INTERVAL : false;
      },
      retry: 5,
    });

  // Extract the actual rooms data from the poll response (handles nested { data: { status, results } } from API)
  const roomsData = useMemo(() => {
    if (cacheKey && localCacheData && isBulkManual) {
      const cached = getPollPayload(localCacheData as PollResponse);
      return cached.status === 'SUCCESS' ? cached.results : undefined;
    }

    const payload = getPollPayload(pollResponse);
    return payload.status === 'SUCCESS' ? payload.results : undefined;
  }, [pollResponse, cacheKey, localCacheData, isBulkManual]);

  useEffect(() => {
    //clean-up
    return () => {
      updateSharedData({
        meetingRooms: [],
        meetingRoomObj: {},
      });
    };
  }, [updateSharedData]);

  useEffect(() => {
    const unregisterBookedSlot = registerForUpdates('bookedSlot', (data) => {
      const { bookedSlot } = data?.fullData ?? {};
      if (bookedSlot?.start && bookedSlot?.end) {
        setStartTime(bookedSlot?.start);
        setEndTime(bookedSlot?.end);
      }
    });

    return () => {
      unregisterBookedSlot();
    };
  }, [registerForUpdates]);

  // Convert our roomsData to formatted options
  const formattedOptions = useMemo(
    () => formatRoomsToOptions(roomsData),
    [roomsData]
  );

  // Format rooms for accordion display
  const accordionGroups = useMemo(
    () => formatRoomsToAccordionGroups(roomsData),
    [roomsData]
  );

  // Initialize selected options when roomsData and field.value are available
  useEffect(() => {
    if (formattedOptions.length > 0 && value) {
      const valueArray = Array.isArray(value) ? value : [value];
      if (valueArray.length > 0) {
        const newSelectedOptions: Option[] = [];

        // Find matching options from the formatted options
        formattedOptions.forEach((group) => {
          group.options.forEach((option) => {
            if (valueArray.includes(option.value)) {
              newSelectedOptions.push(option);
            }
          });
        });

        if (newSelectedOptions.length > 0) {
          setSelectedOptions(newSelectedOptions);

          // Update shared data
          const getMeetingRoomObj = (selectedRooms: Option[]) => {
            const obj = {};
            selectedRooms?.forEach((room) => {
              obj[room?.value] = room.room_name;
            });
            return obj;
          };

          if (isMulti) {
            updateSharedData({
              meetingRooms: newSelectedOptions.map((room) => room.value),
              meetingRoomObj: getMeetingRoomObj(newSelectedOptions),
            });
          } else {
            const selected = newSelectedOptions[0];
            updateSharedData({
              meetingRooms: [selected.value],
              meetingRoomObj: { [selected.value]: selected.room_name },
            });
          }
        }
      }
    }
  }, [formattedOptions, value, isMulti, updateSharedData]);

  // Handle input change
  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    return newValue;
  };

  // Handle option selection
  const handleChange = useCallback(
    (selectedOptions: Option[] | null) => {
      const options = selectedOptions || [];
      const newValues = isMulti
        ? options.map((option) => option.value)
        : options.length > 0
          ? options[0].value
          : '';

      setValue(newValues);
      setTouched(true);
      setSelectedOptions(options);

      const getMeetingRoomObj = (selectedRooms: Option[]) => {
        const obj = {};
        selectedRooms?.forEach((room) => {
          obj[room?.value] = room.room_name;
        });

        return obj;
      };

      if (isMulti) {
        updateSharedData({
          meetingRooms: options.map((room) => room.value),
          meetingRoomObj: getMeetingRoomObj(options),
          clearCustomSlots: true,
        });
      } else if (options.length > 0) {
        const selected = options[0];
        updateSharedData({
          meetingRooms: [selected.value],
          meetingRoomObj: { [selected.value]: selected.room_name },
          clearCustomSlots: true,
        });
      } else {
        updateSharedData({
          meetingRooms: [],
          meetingRoomObj: {},
          clearCustomSlots: true,
        });
      }
    },
    [isMulti, setValue, setTouched, updateSharedData]
  );

  const pollStatus = getPollPayload(pollResponse)?.status;
  const isLoading = isUuidLoading || isPollLoading || pollStatus === 'PENDING';

  // Check if any currently selected room is unavailable
  const hasUnavailableRoom =
    !isLoading &&
    (isEdit ? hasValuesChanged() : true) &&
    !!selectedOptions.length &&
    selectedOptions?.some((option) => {
      return option.available === false;
    });

  return (
    <FormControl isInvalid={touched && !!error} mb="0.75em">
      <FormCustomLabel item={{ label: 'Meeting room', name: 'meetingRoom' }} />
      <Select
        isLoading={isLoading}
        loadingMessage={() => <CustomLoadingMessage />}
        value={selectedOptions}
        options={formattedOptions}
        styles={{
          ...getCustomStyles(),
          menu: (provided) => ({
            ...provided,
            padding: 0,
          }),

          multiValue: (base, state, newStyles) => ({
            ...base,
            fontSize: fontSizes['sm'],
            fontWeight: fontWeights['medium'],
            color: colors['secondary-700'],
            opacity: state.isDisabled ? 0.5 : 1,
            background: '#fff',
            borderRadius: '6px',
            ...newStyles,
            border:
              state.data.available === false
                ? '1px solid #F8636B !important'
                : '1px solid #DBDBDB',
          }),
        }}
        hasError={hasUnavailableRoom}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder="Search"
        components={{
          Menu: SchMeetingRoomMenu,
          IndicatorSeparator: CustomIndicatorSeparator,
          MultiValueLabel: CustomMultiValueLabel,
        }}
        accordionGroups={accordionGroups}
        isDisabled={isLoading}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        isSearchable={true}
        filterOption={customFilterOption}
        inputValue={inputValue}
        onInputChange={handleInputChange}
      />
      {hasUnavailableRoom && (
        <Box mt={2} p={3} bg="secondary-50" borderRadius="md">
          <Flex alignItems="center" gap="6px">
            <Icon as={FaceSad} w="16px" h="16px" color="secondary-700" />
            <KText color="secondary-900" fontWeight="medium">
              Meeting room is not available
            </KText>
          </Flex>
          <KText color="secondary-900" fontSize="sm" mt={1}>
            This room isn&apos;t available at the selected date and time. Choose
            a different room or another time
          </KText>
        </Box>
      )}
      {touched && error && (
        <FormErrorMessage flexDir="column" alignItems="flex-start">
          <KText color="ats-negative">{error}</KText>
        </FormErrorMessage>
      )}
    </FormControl>
  );
};
